export interface NotificationOpen {
  item: string;
  student: string;
}

export const MAX_ITEM_LENGTH = 128;
export const MAX_STUDENT_LENGTH = 128;
const BUFFER_TTL_MS = 30_000;

const isReference = (value: unknown, max: number): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= max;

// The native shell posts {type:'NOTIFICATION_OPENED', item, student} when a
// push is tapped. Both fields are opaque pointers; nothing here trusts them.
export const parseNotificationOpenMessage = (
  raw: unknown
): NotificationOpen | null => {
  let data: unknown = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;

  const { type, item, student } = data as Record<string, unknown>;
  if (type !== 'NOTIFICATION_OPENED') return null;
  if (!isReference(item, MAX_ITEM_LENGTH)) return null;
  if (!isReference(student, MAX_STUDENT_LENGTH)) return null;
  return { item, student };
};

let pending: { open: NotificationOpen; expiresAt: number } | null = null;
const listeners = new Set<() => void>();

const onMessage = (event: Event) => {
  if (typeof window === 'undefined' || !window.ReactNativeWebView) return;
  // The shell dispatches its messages with no source or origin. Anything that
  // has either is a frame or another window, not the shell.
  const { source, origin, data } = event as MessageEvent;
  if (source || origin) return;

  const open = parseNotificationOpenMessage(data);
  if (!open) return;
  pending = { open, expiresAt: Date.now() + BUFFER_TTL_MS };
  listeners.forEach(listener => listener());
};

// Installed at import, before React renders: the tap can be delivered while
// the parent is still signing in. Android WebViews deliver on `document`, iOS
// on `window`.
if (typeof window !== 'undefined') {
  window.addEventListener('message', onMessage);
  document.addEventListener('message', onMessage);
}

// One slot, handed out once: a tap is never replayed, and a stale one is gone.
export const takePendingNotificationOpen = (): NotificationOpen | null => {
  const held = pending;
  pending = null;
  if (!held || held.expiresAt <= Date.now()) return null;
  return held.open;
};

export const CARRY_KEY = 'parent.pendingNotificationItem';

// A tap belongs to the session it arrived in: call when the session changes
// hands so it is neither replayed nor handed to someone else.
export const discardNotificationOpen = (): void => {
  pending = null;
  try {
    sessionStorage.removeItem(CARRY_KEY);
  } catch {
    // Storage unavailable: nothing was carried.
  }
};

export const subscribeToNotificationOpen = (
  listener: () => void
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
